import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { parseApiError } from '../lib/parseApiError';
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Tag,
  Euro,
  Package,
  FileText,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListinoPrezziPage() {
  const { isAdmin, user } = useAuth();
  const admin = isAdmin();
  const canEdit = admin || user?.ruolo === 'operatore';
  
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [categorie, setCategorie] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticolo, setEditingArticolo] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleSyncMagazzino = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/listino/sync-magazzino`);
      toast.success(`✅ Import da magazzino: ${data.creati} nuove voci, ${data.aggiornati} aggiornate${data.saltati ? `, ${data.saltati} già presenti` : ''}`);
      fetchArticoli();
      fetchCategorie();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore importazione da magazzino'));
    } finally {
      setSyncing(false);
    }
  };

  const [formData, setFormData] = useState({
    codice: '',
    descrizione: '',
    prezzo: '',
    prezzo_acquisto: '',
    categoria: '',
    unita_misura: 'pz',
    iva_percentuale: '22',
    note: ''
  });

  useEffect(() => {
    fetchArticoli();
    fetchCategorie();
  }, []);

  const fetchArticoli = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/listino`, {
        params: { solo_attivi: false }
      });
      setArticoli(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorie = async () => {
    try {
      const res = await axios.get(`${API}/listino/categorie`);
      setCategorie(res.data);
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.codice || !formData.descrizione || !formData.prezzo) {
      toast.error('Compila codice, descrizione e prezzo');
      return;
    }

    try {
      const payload = {
        ...formData,
        prezzo: parseFloat(formData.prezzo),
        prezzo_acquisto: formData.prezzo_acquisto !== '' ? parseFloat(formData.prezzo_acquisto) : null,
        iva_percentuale: parseFloat(formData.iva_percentuale) || 22,
        categoria: formData.categoria || null
      };

      if (editingArticolo) {
        await axios.put(`${API}/listino/${editingArticolo.id}`, payload);
        toast.success('Articolo aggiornato');
      } else {
        await axios.post(`${API}/listino`, payload);
        toast.success('Articolo creato');
      }

      setDialogOpen(false);
      resetForm();
      fetchArticoli();
      fetchCategorie();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleEdit = (articolo) => {
    setEditingArticolo(articolo);
    setFormData({
      codice: articolo.codice || '',
      descrizione: articolo.descrizione || '',
      prezzo: articolo.prezzo?.toString() || '',
      prezzo_acquisto: articolo.prezzo_acquisto != null ? articolo.prezzo_acquisto.toString() : '',
      categoria: articolo.categoria || '',
      unita_misura: articolo.unita_misura || 'pz',
      iva_percentuale: articolo.iva_percentuale?.toString() || '22',
      note: articolo.note || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo articolo dal listino?')) return;
    try {
      await axios.delete(`${API}/listino/${id}`);
      toast.success('Articolo eliminato');
      fetchArticoli();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const resetForm = () => {
    setEditingArticolo(null);
    setFormData({
      codice: '',
      descrizione: '',
      prezzo: '',
      prezzo_acquisto: '',
      categoria: '',
      unita_misura: 'pz',
      iva_percentuale: '22',
      note: ''
    });
  };

  const filteredArticoli = articoli.filter(a => {
    const matchSearch = 
      a.codice?.toLowerCase().includes(search.toLowerCase()) ||
      a.descrizione?.toLowerCase().includes(search.toLowerCase());
    const matchCategoria = !filtroCategoria || a.categoria === filtroCategoria;
    return matchSearch && matchCategoria;
  });

  // Stats
  const totaleArticoli = articoli.length;
  const articoliAttivi = articoli.filter(a => a.attivo).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="listino-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <Tag className="h-8 w-8" />
            Listino Prezzi
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Catalogo articoli con prezzi predefiniti per i preventivi
          </p>
        </div>
        
        {canEdit && (
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={handleSyncMagazzino}
              disabled={syncing}
              className="rounded-none font-mono uppercase"
              title="Importa nel listino gli articoli presenti in magazzino"
              data-testid="sync-magazzino-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Importo...' : 'Importa da Magazzino'}
            </Button>
            <Button 
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="rounded-none font-mono uppercase"
              data-testid="new-articolo-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Articolo
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-none border-l-4 border-l-primary">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-black">{totaleArticoli}</p>
                <p className="text-xs text-muted-foreground">Totale Articoli</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-black text-green-600">{articoliAttivi}</p>
                <p className="text-xs text-muted-foreground">Attivi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-black text-blue-600">{categorie.length}</p>
                <p className="text-xs text-muted-foreground">Categorie</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Cerca per codice o descrizione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-none"
            data-testid="search-listino"
          />
        </div>
        <Select value={filtroCategoria || "all"} onValueChange={(v) => setFiltroCategoria(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 rounded-none">
            <SelectValue placeholder="Tutte le categorie" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categorie.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="bg-blue-600/10">Codice</th>
              <th className="bg-primary/10">Descrizione</th>
              <th className="bg-green-600/10 text-right">Prezzo</th>
              {admin && <th className="hidden md:table-cell bg-rose-600/10 text-right">Acquisto</th>}
              {admin && <th className="hidden md:table-cell bg-teal-600/10 text-right">Margine</th>}
              <th className="hidden md:table-cell bg-purple-600/10">Categoria</th>
              <th className="hidden md:table-cell bg-amber-600/10">U.M.</th>
              <th className="hidden lg:table-cell">IVA</th>
              {canEdit && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : filteredArticoli.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center text-muted-foreground py-8">
                  {articoli.length === 0 ? 'Nessun articolo nel listino' : 'Nessun articolo trovato'}
                </td>
              </tr>
            ) : (
              filteredArticoli.map((articolo) => (
                <tr key={articolo.id} className={`hover:bg-muted/50 transition-colors ${!articolo.attivo ? 'opacity-50' : ''}`}>
                  <td className="font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-600/5 border-l-4 border-l-blue-600">
                    {articolo.codice}
                  </td>
                  <td className="bg-primary/5 border-l-4 border-l-primary">
                    {articolo.descrizione}
                    {articolo.origine === 'magazzino' && (
                      <Badge className="ml-2 text-[10px] rounded-none bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border border-cyan-500/30" data-testid={`badge-magazzino-${articolo.id}`}>
                        📦 Magazzino
                      </Badge>
                    )}
                    {!articolo.attivo && (
                      <Badge variant="secondary" className="ml-2 text-xs">Disattivato</Badge>
                    )}
                  </td>
                  <td className="text-right font-mono font-bold text-green-600 dark:text-green-400 bg-green-600/5 border-l-4 border-l-green-600">
                    €{articolo.prezzo?.toFixed(2)}
                  </td>
                  {admin && (
                    <td className="hidden md:table-cell text-right font-mono text-rose-600 dark:text-rose-400 bg-rose-600/5 border-l-4 border-l-rose-600" data-testid={`prezzo-acquisto-${articolo.id}`}>
                      {articolo.prezzo_acquisto != null ? `€${articolo.prezzo_acquisto.toFixed(2)}` : '-'}
                    </td>
                  )}
                  {admin && (
                    <td className="hidden md:table-cell text-right font-mono font-bold text-teal-600 dark:text-teal-400 bg-teal-600/5 border-l-4 border-l-teal-600">
                      {articolo.prezzo_acquisto != null && articolo.prezzo != null
                        ? `€${(articolo.prezzo - articolo.prezzo_acquisto).toFixed(2)}`
                        : '-'}
                    </td>
                  )}
                  <td className="hidden md:table-cell bg-purple-600/5 text-purple-600 dark:text-purple-400 border-l-4 border-l-purple-600">
                    {articolo.categoria || '-'}
                  </td>
                  <td className="hidden md:table-cell bg-amber-600/5 text-amber-600 dark:text-amber-400 border-l-4 border-l-amber-600">
                    {articolo.unita_misura}
                  </td>
                  <td className="hidden lg:table-cell">{articolo.iva_percentuale}%</td>
                  {canEdit && (
                    <td>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEdit(articolo)}
                          data-testid={`edit-articolo-${articolo.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(articolo.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-articolo-${articolo.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase tracking-wider">
              {editingArticolo ? 'Modifica Articolo' : 'Nuovo Articolo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase">Codice *</Label>
                <Input
                  value={formData.codice}
                  onChange={(e) => setFormData({...formData, codice: e.target.value.toUpperCase()})}
                  className="rounded-none font-mono"
                  placeholder="ES: PROD001"
                  data-testid="articolo-codice"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Descrizione *</Label>
                <Input
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                  className="rounded-none"
                  placeholder="Descrizione prodotto/servizio"
                  data-testid="articolo-descrizione"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Prezzo *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.prezzo}
                    onChange={(e) => setFormData({...formData, prezzo: e.target.value})}
                    className="rounded-none pl-9"
                    placeholder="0.00"
                    data-testid="articolo-prezzo"
                  />
                </div>
              </div>
              {admin && (
                <div>
                  <Label className="text-xs font-mono uppercase text-rose-600">Prezzo Acquisto</Label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.prezzo_acquisto}
                      onChange={(e) => setFormData({...formData, prezzo_acquisto: e.target.value})}
                      className="rounded-none pl-9"
                      placeholder="Opzionale"
                      data-testid="articolo-prezzo-acquisto"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Visibile solo agli admin (per calcolo guadagno)</p>
                </div>
              )}
              <div>
                <Label className="text-xs font-mono uppercase">Categoria</Label>
                <Input
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="rounded-none"
                  placeholder="Es: Assistenza, Hardware"
                  list="categorie-list"
                  data-testid="articolo-categoria"
                />
                <datalist id="categorie-list">
                  {categorie.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Unità Misura</Label>
                <Select 
                  value={formData.unita_misura} 
                  onValueChange={(v) => setFormData({...formData, unita_misura: v})}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="pz">Pezzo (pz)</SelectItem>
                    <SelectItem value="h">Ora (h)</SelectItem>
                    <SelectItem value="gg">Giorno (gg)</SelectItem>
                    <SelectItem value="mese">Mese</SelectItem>
                    <SelectItem value="anno">Anno</SelectItem>
                    <SelectItem value="kg">Kilogrammo (kg)</SelectItem>
                    <SelectItem value="m">Metro (m)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">IVA %</Label>
                <Select 
                  value={formData.iva_percentuale} 
                  onValueChange={(v) => setFormData({...formData, iva_percentuale: v})}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="22">22%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="4">4%</SelectItem>
                    <SelectItem value="0">Esente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Note</Label>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  className="rounded-none"
                  placeholder="Note aggiuntive..."
                  data-testid="articolo-note"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="rounded-none"
              >
                Annulla
              </Button>
              <Button type="submit" className="rounded-none font-mono uppercase">
                {editingArticolo ? 'Aggiorna' : 'Crea Articolo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
